import { throttle } from 'lodash'
import { TimeConstants } from '@core/Constants'

export enum FileTransmissionError {
  UNKNOWN,
  /**
   * 上传初始化失败
   */
  INIT_FAILED,
  /**
   * 文件的分片在上传是发生错误
   */
  MULTIPART_UPLOADING_FAILED,
  /**
   * 请求恢复上传/续传时发生错误
   */
  RESUME_REQUEST_FAILED,
  /**
   * 文件分片上传时在结束上传时发生错误
   */
  COMPLETE_REQUEST_FAILED,
  /**
   * 文件分片上传时重新上传时发生错误
   */
  RESUME_FAILED,
}

export interface IFileTransmissionProgressListener {
  /**
   * Called when progress start
   *
   * @param id the identity of uploading task
   */
  onStart(id: string): void

  /**
   * Called when file transmission progress changed
   *
   * @param transmitted already transmitted in bytes
   * @param total file total size in bytes
   */
  onProgressChanged(transmitted: number, total: number, progressMap?: Map<number, number>): void

  /**
   * Called when transmission completed
   *
   * @param fileId 文件上传结束后返回的文件访问标识，对于分片上传时会有此值.
   */
  onCompleted(fileId?: string): void

  /**
   * Called when progress be cancelled
   */
  onCancelled(isRemote: boolean): void

  /**
   * Called when error occurred in progress
   *
   * @param error
   */
  onError(error: FileTransmissionError): void
}

/**
 * Default file transmission progress listener proxy, which throttle the notify rate
 */
export class DefaultFileTransmissionProgressListener implements IFileTransmissionProgressListener {
  readonly outerProgressChange: IFileTransmissionProgressListener

  constructor(listener: IFileTransmissionProgressListener) {
    this.outerProgressChange = {
      onStart: listener.onStart.bind(listener),
      onCompleted: listener.onCompleted.bind(listener),
      onCancelled: listener.onCancelled.bind(listener),
      onError: listener.onError.bind(listener),
      onProgressChanged: throttle(listener.onProgressChanged.bind(listener), TimeConstants.DEFAULT_THROTTLING_IN_MS),
    }
  }

  onProgressChanged(transmitted: number, total: number, progressMap?: Map<number, number>): void {
    this.outerProgressChange.onProgressChanged(transmitted, total, progressMap)
  }

  onCancelled(isRemote: boolean): void {
    this.outerProgressChange.onCancelled(isRemote)
  }

  onCompleted(fileId?: string): void {
    this.outerProgressChange.onCompleted(fileId)
  }

  onError(error: FileTransmissionError): void {
    this.outerProgressChange.onError(error)
  }

  onStart(id: string): void {
    this.outerProgressChange.onStart(id)
  }
}

export class MultipartUploadProgressListener extends DefaultFileTransmissionProgressListener {
  readonly #totalSize: number
  #transmittedSize = 0
  #startThreshold = 0
  #currentChunkSize = 0
  #prevChunkSize = 0
  constructor(totalSize: number, listener: IFileTransmissionProgressListener) {
    super(listener)
    this.#totalSize = totalSize
  }

  onProgressChanged(transmitted: number, total: number) {
    const rawSize = total > 0 ? Math.ceil(this.#currentChunkSize * ((transmitted - this.#prevChunkSize) / total)) : 0

    this.#prevChunkSize = transmitted === total ? 0 : transmitted
    const preSize = this.#transmittedSize + rawSize

    if (this.#transmittedSize < this.#totalSize && preSize >= this.#startThreshold) {
      this.#transmittedSize = preSize <= this.#totalSize ? preSize : this.#totalSize

      super.onProgressChanged(this.#transmittedSize, this.#totalSize)
    }
  }

  set transmittedSize(size: number) {
    this.#transmittedSize = size
  }

  set startThreshold(startThreshold: number) {
    this.#startThreshold = startThreshold
  }

  set currentChunkSize(size: number) {
    this.#currentChunkSize = size
  }
}
